/**
 * 新規記事作成 — everything the article is written from, in the order it is decided.
 *
 * The order is the point. A writer arriving here is answering four questions,
 * and each one only makes sense once the one before it is settled:
 *
 *   1. WHO is this for   - which clinic and which branch. Shown as facts, not as
 *                          a form: the address, the reservation URL and the
 *                          opening hours are what the article may state, and a
 *                          blank among them is the reason a finished article
 *                          later says nothing about access. Seeing them before
 *                          generating is cheaper than seeing them afterwards.
 *   2. WHAT is it about  - the keyword it is written to rank for, the ones it
 *                          must also mention, the treatment, the working title.
 *   3. WHO is reading it - one persona, from the approved set.
 *   4. HOW should it read - tone, length, and anything else in the writer's own
 *                          words.
 *
 * Nothing here generates. It assembles a brief and creates the draft; STEP 1 -
 * search intent, titles, headings - happens on the next screen, where the writer
 * can see the analysis before anything long is written.
 */
import { useEffect, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useOutletContext } from 'react-router';
import type {
  CatalogItem,
  ListResponse,
  PlatformDefaultItem,
  Session,
  Store,
  StoreDetail,
} from '../api.ts';
import { ApiError, get, post } from '../api.ts';
import { Card, Empty } from '../components/ui.tsx';
import type { ClinicSettingsData } from './ClinicSettings.tsx';
import { Select } from '../components/Select.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { DateInput } from '../components/DateInput.tsx';
import { MaterialScope, type MaterialScopeValue } from '../components/MaterialScope.tsx';

interface LoaderData {
  /** JMC's lists (共通マスタ), minus anything this clinic hid. */
  tones: ListResponse<PlatformDefaultItem>;
  articleTypes: ListResponse<PlatformDefaultItem>;
  personas: ListResponse<CatalogItem>;
  stores: ListResponse<Store>;
  treatments: ListResponse<CatalogItem>;
  strengths: ListResponse<CatalogItem>;
  supervisors: ListResponse<CatalogItem>;
  clinic: ClinicSettingsData;
  /** The selected store's own facts. Null when no store is selected yet. */
  store: StoreDetail | null;
}

const QUICK_INSTRUCTIONS = [
  '専門性を強調',
  '予約導線を意識',
  '比較表を含める',
  '費用の内訳にふれる',
  'ダウンタイムを丁寧に',
  '初めての人向け',
];

/**
 * What is offered when JMC's list is empty or unreachable.
 *
 * The lists live in 共通マスタ now - JMC adds a type or a tone without a deploy,
 * and a clinic hides one it never uses. These are the values this product
 * shipped with, kept as the floor: a wizard whose first dropdown is empty is a
 * wizard nobody can finish.
 */
const FALLBACK_ARTICLE_TYPES = [
  { value: 'blog', label: 'ブログ記事' },
  { value: 'column', label: 'コラム' },
  { value: 'case', label: '症例紹介' },
];

/**
 * Tones offered per article.
 *
 * '' is the standing tone - the store's, or the clinic's behind it - and is the
 * default because most articles are not an exception. `custom` opens a box,
 * because the useful instruction is 「専門用語は残して落ち着いた調子で」 and no
 * fixed list carries it.
 */
const FALLBACK_TONES = [
  { value: 'ていねい（ですます）', label: 'ていねい（ですます）' },
  { value: 'やさしく、親しみやすく', label: 'やさしい・親しみやすい' },
  { value: '専門性が伝わる落ち着いた調子', label: '専門的・落ち着いた' },
];

const MAX_INSTRUCTION = 500;
const MAX_TONE = 200;

/** Catalog rows carry open-ended fields; only strings are safe to render. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** One line of the brief. '' is unset, and says so rather than showing blank. */
function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="muted small">{label}</dt>
      <dd style={{ margin: 0 }}>{value ? value : <span className="muted">未登録</span>}</dd>
    </div>
  );
}

export default function ArticleNew() {
  const { tones, articleTypes, personas, stores, treatments, strengths, supervisors, clinic, store } =
    useLoaderData<LoaderData>();
  const session = useOutletContext<Session>();
  const navigate = useNavigate();

  /*
   * The offered lists, from 共通マスタ with the shipped values as the floor.
   *
   * The stored value is the payload's and the label is the row's, so renaming an
   * option never rewrites what is already saved on an article.
   */
  const optionsOf = (
    rows: ListResponse<PlatformDefaultItem>,
    fallback: Array<{ value: string; label: string }>,
  ) =>
    rows.items.length > 0
      ? rows.items.map((row) => ({
          value: typeof row.payload.value === 'string' ? row.payload.value : row.label,
          label: row.label,
        }))
      : fallback;

  const articleTypeOptions = optionsOf(articleTypes, FALLBACK_ARTICLE_TYPES);
  const toneOptions = [
    // The standing tone and the free-text escape hatch are the screen's own, not
    // JMC's: one means 「何も指定しない」 and the other opens a box.
    { value: '', label: 'クリニックの標準' },
    ...optionsOf(tones, FALLBACK_TONES),
    { value: 'custom', label: '自由入力…' },
  ];

  /*
   * The store, chosen for them wherever there is nothing to choose.
   *
   * The session's own store first - a store user has exactly one and it is
   * selected at sign-in - then the single option when a clinic has one store.
   * Only a clinic admin with several stores is actually being asked a question.
   */
  const [storeId, setStoreId] = useState(
    session.activeStoreId ?? (stores.items.length === 1 ? stores.items[0]!.id : ''),
  );
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [treatmentId, setTreatmentId] = useState('');
  const [articleType, setArticleType] = useState('blog');
  /*
   * Which approved material this article is written from. Both null - "send
   * everything in scope" - which is what every article did before this existed,
   * and remains the right default: an article is usually about the store, not
   * about a shortlist of its facts.
   */
  const [scope, setScope] = useState<MaterialScopeValue>({
    strengthScope: {},
    supervisorIds: null,
  });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const [tonePreset, setTonePreset] = useState('');
  const [customTone, setCustomTone] = useState('');
  const [targetLength, setTargetLength] = useState('');
  const [publishOn, setPublishOn] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Everything about the selected store, refetched when the selection changes.
   *
   * The loader can only scope to the session's store, so a clinic admin picking
   * a different one in step 1 was reading the wrong branch's address and being
   * offered the wrong branch's personas - and choosing one produced an article
   * whose generation context reported the choice as missing. Now the screen
   * follows the selection.
   */
  const [detail, setDetail] = useState<StoreDetail | null>(store);
  const [material, setMaterial] = useState({ personas, treatments });
  const [loadingStore, setLoadingStore] = useState(false);
  /** Which store the fields were filled from, so typed values are never lost. */
  const filledFrom = useRef(store?.store.id ?? '');

  useEffect(() => {
    if (!storeId) return;
    if (detail?.store.id === storeId) return;

    let cancelled = false;
    setLoadingStore(true);
    void (async () => {
      try {
        const [next, nextPersonas, nextTreatments] = await Promise.all([
          get<StoreDetail>(`/stores/${storeId}`),
          get<ListResponse<CatalogItem>>(`/personas?storeId=${storeId}`),
          get<ListResponse<CatalogItem>>(`/treatments?storeId=${storeId}`),
        ]);
        if (cancelled) return;
        setDetail(next);
        setMaterial({ personas: nextPersonas, treatments: nextTreatments });
        /*
         * A reference chosen for the previous store belongs to that store, so it
         * is dropped rather than carried across - the generator would report it
         * as missing, which is the failure this screen exists to prevent.
         */
        setPersonaId((current) =>
          nextPersonas.items.some((item) => item.id === current) ? current : null,
        );
        setTreatmentId((current) =>
          nextTreatments.items.some((item) => item.id === current) ? current : '',
        );
      } catch {
        // The facts panel keeps showing what it has; creating still validates
        // server-side, so a failed prefill costs information, not correctness.
      } finally {
        if (!cancelled) setLoadingStore(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, detail?.store.id, personas, treatments]);

  /*
   * The store's own defaults, filled in once per store.
   *
   * Prefilled rather than merely inherited, because the wizard is where somebody
   * decides what this article is about: seeing 「たるみ・ハイフ」 already in the box
   * is what tells them the clinic has defaults at all, and they can edit or clear
   * them. Anything already typed is left alone - a prefill must never overwrite a
   * person's work.
   */
  useEffect(() => {
    const facts = detail?.store;
    if (!facts || filledFrom.current === facts.id) return;
    filledFrom.current = facts.id;
    const defaults = facts.default_keywords ?? facts.clinic_default_keywords;
    setKeywords((current) => (current.length > 0 ? current : (defaults ?? [])));
  }, [detail]);

  /*
   * APPROVED, not "not retired": a draft persona is invisible to the generator,
   * so offering one here produced an article whose context reported the persona
   * it was created with as missing.
   */
  const approvedPersonas = material.personas.items.filter((p) => p.status === 'APPROVED');
  const approvedTreatments = material.treatments.items.filter((t) => t.status === 'APPROVED');
  const ready = Boolean(storeId && personaId && keywords.length > 0);

  const tone = tonePreset === 'custom' ? customTone.trim() : tonePreset;
  const facts = detail?.store ?? null;
  /** The store's own value, then the clinic's - the ladder the generator climbs. */
  const standingTone = facts?.default_tone ?? facts?.clinic_default_tone ?? clinic.default_tone;
  // The store's own value, then the clinic's behind it - the same ladder the
  // generator climbs, shown here so the writer sees what will actually be used.
  /*
   * Only APPROVED rows, like the personas and treatments above: a screen must
   * not offer a choice the generator would then report as missing.
   */
  const approvedStrengths = strengths.items.filter((item) => item.status === 'APPROVED');
  const approvedSupervisors = supervisors.items.filter((item) => item.status === 'APPROVED');

  const region = [facts?.prefecture || clinic.prefecture, facts?.city || clinic.city]
    .filter(Boolean)
    .join('');
  const lengthDefault = clinic.target_length
    ? `${clinic.target_length.toLocaleString('ja-JP')}文字前後（クリニック設定）`
    : '4,000〜5,000文字（既定）';

  /**
   * Create the draft and open it.
   *
   * Everything the four steps collected becomes columns on the article row -
   * including the tone and the length, which the generation reads back. Nothing
   * is held in the browser between here and the plan screen.
   */
  async function createDraft() {
    const length = targetLength.trim() === '' ? null : Number(targetLength);
    if (length !== null && (!Number.isInteger(length) || length < 500 || length > 20_000)) {
      setError('目安文字数は500〜20000の整数で入力してください。');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await post<{ id: string }>('/articles', {
        storeId,
        /*
         * A typed title if there is one, otherwise the keyword as a working
         * title. Either way the next screen proposes alternatives, so this is a
         * starting point rather than a decision.
         */
        // The first keyword doubles as the working title until the plan screen
        // proposes real ones - which is what it did before the second box existed.
        title: title.trim() || keywords[0]!,
        articleType,
        keywords,
        ...(personaId ? { personaId } : {}),
        // Optional here, required by 本文を生成 - which is why it is offered at
        // creation rather than only in the editor.
        ...(treatmentId ? { treatmentId } : {}),
        ...(publishOn ? { publishOn } : {}),
        ...(tone ? { tone: tone.slice(0, MAX_TONE) } : {}),
        ...(length !== null ? { targetLength: length } : {}),
        ...(instruction.trim() ? { generationNote: instruction.trim() } : {}),
        // Only when something was actually chosen: absent means "everything",
        // and sending an explicit null would say the same thing more loudly.
        ...(Object.keys(scope.strengthScope).length > 0
          ? { strengthScope: scope.strengthScope }
          : {}),
        ...(scope.supervisorIds ? { supervisorIds: scope.supervisorIds } : {}),
      });
      // Straight into step 2, and tell it to propose on arrival: that is what
      // pressing this button meant.
      void navigate(`/articles/${created.id}/plan`, { state: { propose: true } });
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields[0] ?? err.message) : '作成できませんでした');
    } finally {
      setCreating(false);
    }
  }

  function appendInstruction(text: string) {
    setInstruction((current) =>
      current.includes(text)
        ? current
        : `${current}${current ? '\n' : ''}${text}`.slice(0, MAX_INSTRUCTION),
    );
  }

  return (
    <>
      {/* 1. Who the article is for. Facts, before any decision is asked for. */}
      <Card
        title="1. 対象クリニック・店舗"
        caption="記事に書けるのは、ここに登録されている情報だけです。"
      >
        <div className="field" style={{ maxWidth: 360 }}>
          <label htmlFor="store">
            対象店舗{stores.items.length > 1 && <span className="req">必須</span>}
          </label>
          {stores.items.length > 1 ? (
            <>
              <Select
                id="store"
                value={storeId}
                onChange={setStoreId}
                options={[
                  ...(storeId ? [] : [{ value: '', label: '店舗を選択' }]),
                  ...stores.items.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
              <span className="field__hint">店舗を変えると、選べる素材も切り替わります。</span>
            </>
          ) : (
            <>
              {/* One store: naming it is the whole of the information. */}
              <p style={{ margin: 0, fontWeight: 500 }}>
                {facts?.name ?? stores.items[0]?.name ?? '—'}
              </p>

            </>
          )}
        </div>

        <dl className="grid grid--3" style={{ margin: 0 }}>
          <Fact label="クリニック" value={clinic.name} />
          <Fact label="運営法人" value={clinic.corporate_name} />
          <Fact label="公式サイト" value={facts?.site_url ?? clinic.site_url} />
          <Fact label="店舗" value={facts?.name ?? '—'} />
          <Fact label="地域（SEO）" value={region} />
          <Fact label="住所" value={facts?.address} />
          <Fact label="アクセス" value={facts?.access_note} />
          <Fact label="診療時間" value={facts?.hours_note} />
          <Fact label="休診日" value={facts?.closed_note} />
          <Fact label="電話" value={facts?.phone} />
          <Fact label="予約URL（CTA）" value={facts?.reservation_url} />
          <Fact label="標準の文体" value={standingTone} />
          <Fact
            label="承認済み素材"
            value={
              facts
                ? `ペルソナ ${approvedPersonas.length}・施術 ${approvedTreatments.length}`
                : null
            }
          />
        </dl>

        {loadingStore && (
          <p className="muted small" style={{ marginTop: 8 }}>
            店舗情報を読み込んでいます…
          </p>
        )}

        {/*
          What is missing, before a generation discovers it.

          A blank reservation URL costs the CTA its link; no approved persona or
          treatment makes 本文を生成 refuse outright (ai/generate.ts REQUIRED),
          and finding that out from a failed generation is finding it out too
          late.
        */}
        {facts && !facts.reservation_url && (
          <p className="alert" role="status" style={{ marginTop: 12 }}>
            予約URLが未登録です。このままでもCTAは作れますが、リンク先は空欄になります。
          </p>
        )}
        {facts && !loadingStore && approvedTreatments.length === 0 && (
          <p className="alert" role="status" style={{ marginTop: 8 }}>
            この店舗に承認済みの施術がありません。生成はできますが、価格や使用機器には触れられません。
          </p>
        )}
      </Card>

      {/* 2. What it is about. */}
      <Card
        title="2. 記事の条件"
        caption="1つ目のキーワードが、検索意図と地域キーワードの起点になります。"
      >
        <div className="grid grid--3">
          {/*
            One list, not two boxes.

            There used to be 「狙うキーワード」 and 「必須キーワード」 side by side,
            and nothing on the screen said which one the article was written to
            rank for - people filled in both and could not tell afterwards. The
            convention every SEO tool uses replaces them: one ordered list whose
            FIRST entry is the primary keyword, movable by pressing another tag.
          */}
          <div className="field field--wide">
            <label htmlFor="keywords">
              キーワード<span className="req">必須</span>
            </label>
            <KeywordInput id="keywords" value={keywords} onChange={setKeywords} primary />
            <span className="field__hint">
              1つ目が狙うキーワード。タグを押すと入れ替わります。
              {region && `／地域は「${region}」`}
            </span>
          </div>

          <div className="field">
            <label htmlFor="title">タイトル</label>
            <input
              id="title"
              maxLength={200}
              value={title}
              placeholder="空欄なら1つ目のキーワードを仮タイトルにします"
              onChange={(e) => setTitle(e.target.value)}
            />
            <span className="field__hint">次の画面で案を3つ作ります。</span>
          </div>

          <div className="field">
            <label htmlFor="treatment">施術</label>
            <Select
              id="treatment"
              value={treatmentId}
              disabled={approvedTreatments.length === 0}
              onChange={setTreatmentId}
              options={[
                { value: '', label: '未選択' },
                ...approvedTreatments.map((treatment) => ({
                  value: treatment.id,
                  label: asText(treatment.name) || '（名称なし）',
                })),
              ]}
            />
            <span className="field__hint">
              {approvedTreatments.length === 0
                ? '承認済みの施術がありません。'
                : '選ぶと価格や使用機器を書けます。'}
            </span>
          </div>

          <div className="field">
            <label htmlFor="type">記事タイプ</label>
            <Select
              id="type"
              value={articleType}
              onChange={setArticleType}
              options={articleTypeOptions}
            />
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="publish">公開想定日</label>
            <DateInput id="publish" value={publishOn} onChange={setPublishOn} />
          </div>
        </div>
      </Card>

      {/* 3. Who is reading it. */}
      <Card title="3. ペルソナ" caption="1件選びます。悩みが検索意図と書き分けに使われます。">
        {approvedPersonas.length === 0 ? (
          <Empty>
            ペルソナが未登録です。「ペルソナ管理」から、想定読者の年代・悩み・トーンを登録してください。
          </Empty>
        ) : (
          <div className="grid grid--3">
            {approvedPersonas.map((persona) => {
              const label = asText(persona.label);
              return (
                <button
                  key={persona.id}
                  type="button"
                  className="pickcard"
                  aria-pressed={personaId === persona.id}
                  onClick={() => setPersonaId(persona.id)}
                >
                  <span className="pickcard__avatar" aria-hidden="true">
                    {label.slice(0, 1) || '—'}
                  </span>
                  <span>
                    <span className="pickcard__name">{label}</span>
                    <span className="muted small" style={{ display: 'block' }}>
                      {[asText(persona.ageRange), asText(persona.gender)]
                        .filter(Boolean)
                        .join('・') || '属性未設定'}
                    </span>
                    <span className="muted small">{asText(persona.concerns)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/*
        Which approved material this article is written from.

        Between the persona and the tone because that is where it belongs in the
        decision: what it is about, who reads it, WHAT IT MAY DRAW ON, how it
        should sound. The default is everything, so somebody who skips this card
        gets exactly what they got before it existed.
      */}
      {(approvedStrengths.length > 0 || approvedSupervisors.length > 0) && (
        <Card
          title="4. AIに渡す情報"
          caption="未選択のままなら、承認済みの情報がすべて渡ります。"
        >
          <MaterialScope
            strengths={approvedStrengths}
            supervisors={approvedSupervisors}
            value={scope}
            onChange={setScope}
          />
        </Card>
      )}

      {/* 5. How it should read. */}
      <Card
        title="5. 文体・分量・追加指示"
        caption="この記事だけの指定です。標準設定は変わりません。"
      >
        <div className="grid grid--3">
          <div className="field">
            <label htmlFor="tone">文体</label>
            <Select
              id="tone"
              value={tonePreset}
              onChange={setTonePreset}
              options={toneOptions}
            />
            <span className="field__hint">標準は「{standingTone ?? 'polite'}」です。</span>
          </div>

          {tonePreset === 'custom' && (
            <div className="field">
              <label htmlFor="tone-custom">文体（自由入力）</label>
              <input
                id="tone-custom"
                maxLength={MAX_TONE}
                value={customTone}
                placeholder="例：専門用語は残しつつ、落ち着いた調子で"
                onChange={(e) => setCustomTone(e.target.value)}
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="length">目安文字数</label>
            <input
              id="length"
              type="number"
              min={500}
              max={20000}
              step={100}
              value={targetLength}
              placeholder="4500"
              onChange={(e) => setTargetLength(e.target.value)}
            />
            <span className="field__hint">空欄なら {lengthDefault}。</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="instruction">追加で伝えたい指示</label>
          <textarea
            id="instruction"
            value={instruction}
            maxLength={MAX_INSTRUCTION}
            placeholder="例：卒業式に向けた施術として、ダウンタイムを短く伝えたい"
            onChange={(e) => setInstruction(e.target.value)}
          />
          <span className="field__hint">
            {/* One text node, untranslated: see KeywordInput for the counter
                a translated page turned into "0 out of 30". */}
            <span translate="no">{`${instruction.length}/${MAX_INSTRUCTION}`}</span>
            {' 文字・データとして扱われ、システム指示は上書きしません'}
          </span>
        </div>

        <p className="muted small" style={{ margin: '0 0 6px' }}>
          よく使う追加指示
        </p>
        <div className="chips">
          {QUICK_INSTRUCTIONS.map((text) => (
            <button
              key={text}
              type="button"
              className="chip"
              onClick={() => appendInstruction(text)}
            >
              {text}
            </button>
          ))}
        </div>
      </Card>

      <Card title="この内容で作成">
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <p className="muted small">
          次の画面で検索意図・タイトル案・見出し案を作ります。本文はそのあとです。
        </p>
        <button
          className="btn"
          type="button"
          disabled={!ready || creating}
          onClick={() => void createDraft()}
        >
          {creating ? '作成中…' : '下書きを作成して次へ'}
        </button>
        {!ready && (
          <span className="muted small" style={{ marginLeft: 10 }}>
            店舗・狙うキーワード・ペルソナが必要です。
          </span>
        )}
      </Card>
    </>
  );
}
